import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Typography } from 'antd';

import type { KeywordGroupsLimits } from '@app/components/Modals/BotDetailsModal/constants/keywordGroups';
import { getKeywordGroupError } from '@app/components/Modals/BotDetailsModal/utils/keywordGroups';

import type { FC, ReactNode } from 'react';

const { Text } = Typography;

interface KeywordGroupsFieldProps {
  // Что делает робот, если ни одной строки нет — дописывается к подсказке
  emptyHint: string;
  emptyWarning: ReactNode;
  limits?: KeywordGroupsLimits;
}

const KEYWORD_GROUPS_HINT =
  'Значения в одной строке перечисляются через запятую и работают по ИЛИ — достаточно одного совпадения. Несколько строк работают по И — должно совпасть хотя бы одно значение в каждой.';

export const KeywordGroupsField: FC<KeywordGroupsFieldProps> = ({
  emptyHint,
  emptyWarning,
  limits,
}) => (
  <div className="keyword-message-robot-keywords">
    <Text strong>Ключевые слова</Text>
    <Text type="secondary" className="keyword-message-robot-keywords-hint">
      {`${KEYWORD_GROUPS_HINT} ${emptyHint}`}
      {limits && ` Не больше ${limits.maxGroups} строк.`}
    </Text>

    <Form.List name="keywordGroups">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <div key={key} className="keyword-message-robot-keyword-row">
              <Form.Item
                {...restField}
                name={name}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Введите хотя бы одно значение или удалите строку',
                  },
                  {
                    validator: (_rule, value: string | undefined) => {
                      const error = getKeywordGroupError(value, limits);

                      return error
                        ? Promise.reject(new Error(error))
                        : Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Например: цена, стоимость" />
              </Form.Item>

              <Button
                type="text"
                danger
                aria-label="Удалить строку"
                className="keyword-message-robot-keyword-remove"
                icon={<DeleteOutlined />}
                onClick={() => remove(name)}
              />
            </div>
          ))}

          {(!limits || fields.length < limits.maxGroups) && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add('')}
            >
              Добавить строку
            </Button>
          )}

          {fields.length === 0 && (
            <Text
              type="warning"
              className="keyword-message-robot-no-keywords-warning"
            >
              {emptyWarning}
            </Text>
          )}
        </>
      )}
    </Form.List>
  </div>
);
